import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { TraineeActivityEventType } from "@fitness-app/data-models/businessEvents/domain/traineeActivity/TraineeActivityEvents";
import { type ClientActivityEvent } from "@fitness-app/data-models/entities/ClientActivity";

import { supabase } from "~/store/initializeStore";
import { traineeKeys } from "./traineeKeys";

let channelSequence = 0;

/**
 * Kanał realtime jednej karty klientki. Odpowiednik `subscribeToTrainee` z app-store, tylko
 * zamiast pisać do Reduxa unieważnia zapytania karty. Nazwa ma sufiks instancji, bo
 * `supabase.channel(name)` oddaje istniejący kanał o tej samej nazwie (np. ten, który
 * builder otwiera przez Redux dla tej samej klientki).
 */
export const useTraineeRealtime = (traineeId: string) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    channelSequence += 1;
    let hadFailure = false;

    const invalidate = (queryKey: readonly unknown[]) => {
      void queryClient.invalidateQueries({ queryKey });
    };

    const channel = supabase
      .channel(`trainee-card-${traineeId}-${channelSequence}`)
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "trainee", filter: `id=eq.${traineeId}` },
        () => invalidate(traineeKeys.profile(traineeId)),
      )
      .on<ClientActivityEvent>(
        "postgres_changes",
        { event: "*", schema: "public", table: "client_activity_event", filter: `traineeId=eq.${traineeId}` },
        (payload) => {
          if (payload.eventType === "DELETE") {
            return;
          }
          if (payload.new.eventName === TraineeActivityEventType.TraineeSentChatMessage) {
            return;
          }
          // TRE-2413: nawodnienie też, bo pod pomiarami siedzą wpisy napojów (rozbicie sumy dnia).
          if (
            payload.new.eventName === TraineeActivityEventType.TraineeAddedWeightMeasurement ||
            payload.new.eventName === TraineeActivityEventType.TraineeTrackedWaterConsumption
          ) {
            invalidate(traineeKeys.measurements(traineeId));
          }
          invalidate(traineeKeys.activityEvents(traineeId));
        },
      )
      // TRE-2413: a drink swapped for another of the same size keeps the day's total, so no water
      // event comes; the new entry itself refreshes the breakdown. INSERT only: DELETE bypasses RLS.
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "hydration_entry", filter: `traineeId=eq.${traineeId}` },
        () => invalidate(traineeKeys.measurements(traineeId)),
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "client_survey", filter: `traineeId=eq.${traineeId}` },
        () => invalidate(traineeKeys.surveys(traineeId)),
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "client_rating_snapshot", filter: `traineeId=eq.${traineeId}` },
        () => invalidate(traineeKeys.rating(traineeId)),
      )
      .subscribe((status) => {
        if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
          hadFailure = true;
          return;
        }

        // realtime-js sam dołącza kanał ponownie; zmiany z przerwy nie przyjdą, więc dociągamy całą kartę.
        if (status === "SUBSCRIBED" && hadFailure) {
          hadFailure = false;
          invalidate(traineeKeys.all(traineeId));
        }
      });

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [queryClient, traineeId]);
};
